import { Container, PillButton, SectionHeading } from "@/components/common/ui";
import { StepList } from "@/components/common/StepList";
import { SellerSavings } from "@/components/calculators/SellerSavings";
import { SellerNetProceeds } from "@/components/calculators/SellerNetProceeds";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { HomeValueTool } from "@/components/guides/HomeValueTool";
import { OnboardingQuestionnaire } from "@/components/tools/OnboardingQuestionnaire";
import { SchedulingActions } from "@/components/tools/SchedulingActions";
import { IMAGES, SELLER_STEPS } from "@/lib/site";

export default function SellersPage() {
  return (
    <div data-testid="sellers-page">
      <section className="relative overflow-hidden bg-neutral-900">
        <img src={IMAGES.pool} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/10" />
        <Container className="relative py-20 sm:py-28">
          <p className="eyebrow !text-brass">Home Sellers Guide for Virginia</p>
          <h1 className="mt-4 max-w-2xl font-display text-5xl font-normal leading-[1.04] text-white sm:text-6xl">Never pay 6% again to sell your property.</h1>
          <p className="mt-5 max-w-xl text-[17px] text-white/85">
            Be certain to ask Rob about our Seller's Advantage Discount Listing Program. List and sell your property for 4.75% total commission — and save thousands on the sale of your home.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <PillButton href="#market-analysis" tone="light" testId="sellers-fma">Free market analysis</PillButton>
            <PillButton href="#savings" tone="ghostLight" arrow={false} testId="sellers-savings-link">Calculate savings</PillButton>
          </div>
        </Container>
      </section>

      <section id="savings" className="scroll-mt-20 py-16 sm:py-20"><Container><SellerSavings /></Container></section>

      <section className="bg-mist py-16 sm:py-20" data-testid="seller-net-section"><Container><SectionHeading eyebrow="Seller planning" title="Know the number you can move with." subtitle="Start with a transparent net-proceeds estimate before we prepare your exact closing statement." /><div className="mt-8"><SellerNetProceeds /></div></Container></section>

      <section id="pcs-out" className="scroll-mt-20 pb-16 sm:pb-20">
        <Container className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-md border border-neutral-200 p-8">
            <p className="eyebrow">PCS orders out?</p>
            <h2 className="mt-3 text-3xl font-normal">Sell it, or keep it as a rental.</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-neutral-600">
              Whether marketing your current home, handling the property management and rental of your current home, or helping you buy or build your next home — we will custom tailor a marketing plan to best fit your needs. And you will never feel pressured.
            </p>
          </div>
          <div className="overflow-hidden rounded-md bg-ink text-white" data-testid="we-sell-you-vacation">
            <img src={IMAGES.dock} alt="" className="h-44 w-full object-cover opacity-80" />
            <div className="p-8">
              <p className="eyebrow !text-brass">We Sell — You Vacation</p>
              <h2 className="mt-3 text-3xl font-normal">Relax while we sell.</h2>
              <p className="mt-3 text-[16px] leading-relaxed text-white/80">
                Need to sell but don't want the hassle that comes with staging, listing, cleaning and selling? You take off for the weekend while we conduct an open house, prepare your home, and take multiple offers. No headaches from jetting out the door because you have another showing in 10 minutes. Let us do it all for you while you take a break!
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-mist py-16 sm:py-20">
        <Container className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
          <div className="lg:sticky lg:top-24 lg:self-start">
            <SectionHeading eyebrow="Steps for selling your home" title="Know the next move before you list"
              subtitle="There are a lot of moving pieces involved in selling your home. The Richmond and Hampton Roads areas are a great place to be as a home seller." />
          </div>
          <StepList steps={SELLER_STEPS} testId="seller-steps" />
        </Container>
      </section>

      <section id="market-analysis" className="scroll-mt-20 py-16 sm:py-20">
        <Container className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <SectionHeading eyebrow="Free market analysis" title="Find your home's value in today's market, for free."
              subtitle="Want to know what the neighborhood sales data shows for the last 6 months? Just ask!" />
            <p className="mt-6 text-[15px] text-neutral-600">A tradition of bringing 99% of ask to our seller clients, and homes sold for 98% of asking price.</p>
          </div>
          <HomeValueTool />
        </Container>
      </section>
      <section className="bg-mist py-16 sm:py-20" data-testid="seller-onboarding-section"><Container className="grid gap-10 lg:grid-cols-2 lg:items-start"><div><SectionHeading eyebrow="Seller onboarding" title="Put the essentials in one place." subtitle="Tell us about the property, your move and what matters before your valuation appointment." /><div className="mt-8"><OnboardingQuestionnaire mode="seller" testId="seller-onboarding" /></div></div><div className="lg:pt-4"><SectionHeading eyebrow="Meet on your terms" title="Schedule your showing or consultation." subtitle="Choose a preferred time online. Our team will confirm the details with you." /><div className="mt-8"><SchedulingActions audience="seller" testId="seller-scheduling" /></div><div className="mt-8 rounded-md border border-neutral-200 bg-white p-6 sm:p-8"><EnquiryForm kind="market-analysis" extras={["address", "propertyType"]} submitLabel="Request a detailed valuation" testIdPrefix="fma" messagePlaceholder="Timing, upgrades, PCS date…" /></div></div></Container></section>
    </div>
  );
}
